import React from 'react';
import { JcCommonProps, JcUtils } from '../utils/JcUtils';

/** One photo in a {@link JcGallery} / {@link JcLightbox}. */
export interface JcGalleryImage {
    readonly id: string;
    /** The grid thumbnail. */
    readonly thumbURL: string;
    /** The lightbox image. */
    readonly fullURL: string;
    /** Intrinsic dimensions of `fullURL`; reserve space and set the aspect ratio. */
    readonly width: number;
    readonly height: number;
    readonly alt: string;
    readonly caption: string | null;
}

export interface JcGalleryProps extends JcCommonProps {
    readonly images: ReadonlyArray<JcGalleryImage>;
    /** Opens the photo at `index`, typically in a `JcLightbox`. */
    readonly onOpen: (index: number) => void;
    readonly ariaLabel: string;
    /** Minimum thumbnail width (px) before the grid drops a column. Defaults to 220. */
    readonly minItemWidth?: number;
    /** Photos above the fold, loaded eagerly; the rest load lazily. Defaults to 4. */
    readonly eagerCount?: number;
}

type JcCssVars = React.CSSProperties & Record<`--${string}`, string | number>;

/**
 * A responsive grid of uniformly cropped thumbnails (`--jc-gallery-aspect-ratio`),
 * each a button that opens the photo; captions show beneath their thumbnail.
 */
export const JcGallery = (props: JcGalleryProps): React.ReactElement => {
    const { images, onOpen, ariaLabel, minItemWidth = 220, eagerCount = 4 } = props;
    const commonProps = JcUtils.commonProps(props, ['jc-gallery']);
    const style: JcCssVars = { ...props.style, '--jc-gallery-min-item-width': `${minItemWidth}px` };

    return (
        <ul {...commonProps} style={style} aria-label={ariaLabel}>
            {images.map((image, index) => (
                <li key={image.id} className="jc-gallery-item">
                    <figure className="jc-gallery-figure">
                        <button
                            type="button"
                            className="jc-gallery-button"
                            aria-label={`Open photo ${index + 1} of ${images.length}`}
                            onClick={() => onOpen(index)}
                        >
                            <img
                                className="jc-gallery-image"
                                src={image.thumbURL}
                                alt={image.alt}
                                width={image.width}
                                height={image.height}
                                loading={index < eagerCount ? undefined : 'lazy'}
                                decoding="async"
                            />
                        </button>
                        {image.caption && <figcaption className="jc-gallery-caption">{image.caption}</figcaption>}
                    </figure>
                </li>
            ))}
        </ul>
    );
};
